/**
 * PublishModal — the #665 draft→publish commitment point (spec §3:
 * "publish modal: notify all / only-changed / silently").
 *
 * The manager noodles in drafts; THIS is where the week becomes real for
 * staff. The three notify choices ride the POST body (`notify`) — the
 * server enforces them, so "publish silently" can never quietly digest the
 * roster (P5: the surface must do exactly what it says). A silent publish
 * still counts as ANNOUNCED server-side (notified_at is stamped), so a
 * later edit delta-notifies exactly like any other change.
 *
 * Delta-notify is the default (Deputy's "publish updates"): the everyday
 * re-publish after a tweak pings only the people whose week changed.
 *
 * The caller freezes {from,to,draftCount} at open and passes the frozen
 * values — the modal always names exactly what confirm will publish, even
 * if the week behind it changes.
 *
 * Focus is TRAPPED inside the dialog (Dog ID Card pattern, PR #590) and
 * RESTORED to the trigger on close — a keyboard user can't tab out behind
 * the overlay and mutate the page the modal is speaking about.
 *
 * Principles served: P2 (one obvious commit action; choices in plain
 * words), P5 (the lens honesty line — publishing covers the whole week,
 * not the currently filtered lane).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublishNotifyMode } from '../../lib/api.js';

export interface PublishModalProps {
  readonly draftCount: number;
  /** Human week range, e.g. "Mon, Aug 3 – Sun, Aug 9". */
  readonly weekLabel: string;
  /** True when a layer chip other than All is active — the modal must say
   *  publishing is week-wide, not lane-wide (P5). */
  readonly laneFiltered: boolean;
  readonly publishing: boolean;
  readonly error: string | null;
  readonly onConfirm: (mode: PublishNotifyMode) => void;
  readonly onClose: () => void;
}

export function PublishModal({
  draftCount,
  weekLabel,
  laneFiltered,
  publishing,
  error,
  onConfirm,
  onClose,
}: PublishModalProps): JSX.Element {
  const [mode, setMode] = useState<PublishNotifyMode>('changed');
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstRadio = useRef<HTMLInputElement>(null);

  // Focus moves in on open and RETURNS to the trigger on unmount — React
  // does not restore it for us (review minor a; PR #590 pattern).
  useEffect(() => {
    const trigger =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    firstRadio.current?.focus();
    return () => trigger?.focus();
  }, []);

  // Esc closes; Tab is trapped within the dialog (PR #590 pattern).
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!publishing) onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose, publishing],
  );

  const choice = (
    value: PublishNotifyMode,
    label: string,
    hint: string,
    ref?: React.RefObject<HTMLInputElement>,
  ): JSX.Element => {
    const id = `publish-notify-${value}`;
    return (
      <label className="ferro-publish-modal__choice" htmlFor={id}>
        <input
          id={id}
          ref={ref}
          type="radio"
          name="publish-notify"
          value={value}
          checked={mode === value}
          onChange={() => setMode(value)}
        />
        <span>
          <span className="ferro-publish-modal__choice-label">{label}</span>
          <span className="ferro-publish-modal__choice-hint">{hint}</span>
        </span>
      </label>
    );
  };

  return (
    <div
      className="ferro-publish-modal__overlay"
      data-testid="publish-modal-overlay"
    >
      <button
        type="button"
        aria-label="Close publish schedule"
        data-testid="publish-modal-backdrop"
        onClick={onClose}
        disabled={publishing}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'default',
        }}
      />
      <div
        className="ferro-publish-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-modal-title"
        data-testid="publish-modal"
        ref={dialogRef}
        onKeyDown={onKeyDown}
        style={{ position: 'relative' }}
      >
        <h2 id="publish-modal-title" className="ferro-publish-modal__title">
          Publish {draftCount} draft {draftCount === 1 ? 'shift' : 'shifts'}
        </h2>
        <p className="ferro-muted">
          Staff can’t see drafts. Publishing makes every draft in {weekLabel}{' '}
          part of the real schedule.
          {laneFiltered
            ? ' This publishes the WHOLE week — every job function, not just the lane you’re viewing.'
            : ''}
        </p>

        <fieldset className="ferro-publish-modal__choices">
          <legend className="ferro-publish-modal__legend">
            Who should hear about it?
          </legend>
          {choice(
            'changed',
            'Notify only people whose shifts changed',
            'One digest each — new or edited shifts since their last notice. Silent publishes count as noticed.',
            firstRadio,
          )}
          {choice(
            'all',
            'Notify everyone on this week’s schedule',
            'Everyone with shifts this week gets their full week again.',
          )}
          {choice(
            'none',
            'Publish silently — no notifications',
            'Shifts become visible, nobody gets a ping. If you edit one later, that change still notifies its person.',
          )}
        </fieldset>

        {error ? (
          <div className="ferro-dashboard__error" role="alert">
            <p>{error}</p>
          </div>
        ) : null}

        <div className="ferro-form__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            disabled={publishing}
            aria-busy={publishing}
            onClick={() => onConfirm(mode)}
            data-testid="publish-confirm"
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            disabled={publishing}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
